import { LocaleProvider } from 'odc-shared/components';
import { useCallback, useEffect, useMemo, useState } from 'react';

import useMobxActions from '@src/hooks/useMobxActions';
import useMobxSelector from '@src/hooks/useMobxSelector';
import useNav from '@src/hooks/useNav';
import { BASE_LANGUAGE, normalizeLanguage } from '@src/pages/supportCenter/constants/language';
import { getCategoryFirstPid } from '@src/utils/helpCenter';

import { PlatformCategory, useSupportHomeSearch, useSupportSearchPagination } from './hooks';
import SupportHome from './widgets/supportHome';
import SupportSearch from './widgets/supportSearch';

import './index.scss';

const Support = () => {
  const [showSearch, setShowSearch] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('allResults') === 'true';
  });
  const [showGlobalSearch, setShowGlobalSearch] = useState(false);
  const nav = useNav();
  const actions = useMobxActions();

  // 共享的 keyword 状态
  const [keyword, setKeyword] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('keyword') || '';
  });

  // 分类筛选状态
  const [selectedCategory, setSelectedCategory] = useState<PlatformCategory>('all');

  // 「全部结果页」的搜索语言：跟随发起端
  //   - 帮助中心内页点 View more 跳进来 → URL 带 language，用它
  //   - 首页 Banner 搜索原地切视图 → URL 没有 language，固定英文（首页全程英文）
  // 未启用的语言会被 normalizeLanguage 收敛成英文，老链接不会白屏
  const [searchLanguage, setSearchLanguage] = useState(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return normalizeLanguage(searchParams.get('language'));
  });

  // SupportHome 搜索 hook - 只加载前 10 个
  const homeSearch = useSupportHomeSearch(showGlobalSearch ? keyword : '');

  // SupportSearch 分页搜索 hook - 支持分页加载
  const paginationSearch = useSupportSearchPagination(showSearch ? keyword : '', searchLanguage);

  // 从store获取数据
  const catalogsByPlatform = useMobxSelector((stores) => stores.helpCenterStore.catalogsByPlatform);
  // 已缓存目录对应的语言：判断建议列表是不是英文那一份（P7）
  const catalogsLanguage = useMobxSelector((stores) => stores.helpCenterStore.catalogsLanguage);

  useEffect(() => {
    actions.helpCenterStore.fetchCatalogs('publisher');
    actions.helpCenterStore.fetchCatalogs('advertiser');
    actions.helpCenterStore.fetchPopularPosts();
  }, [actions]);

  // 生成搜索建议 - 使用两个平台的一级目录
  const searchSuggestions = useMemo(() => {
    // 首页固定英文：缓存里的目录还是别的语言那一份（刚从帮助中心中文切回来）时不给建议，
    // 否则弹窗里会短暂露出中文目录名（P7）
    if (catalogsLanguage.publisher !== BASE_LANGUAGE && catalogsLanguage.advertiser !== BASE_LANGUAGE) {
      return [];
    }

    const publisherCatalogs = catalogsByPlatform.publisher || [];
    const advertiserCatalogs = catalogsByPlatform.advertiser || [];

    const groups = [];

    // User Growth 平台建议
    if (advertiserCatalogs.length > 0) {
      groups.push({
        title: 'User Growth',
        suggestions: advertiserCatalogs.map((catalog, index) => {
          return {
            id: `advertiser_${catalog.id || index}`,
            label: catalog.name || 'Untitled',
            url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=advertiser`,
          };
        }),
      });
    }

    // Monetization 平台建议
    if (publisherCatalogs.length > 0) {
      groups.push({
        title: 'Monetization',
        suggestions: publisherCatalogs.map((catalog, index) => ({
          id: `publisher_${catalog.id || index}`,
          label: catalog.name || 'Untitled',
          url: `/supportcenter?pid=${getCategoryFirstPid(catalog)}&platform=publisher`,
        })),
      });
    }

    return groups;
  }, [catalogsByPlatform]);

  // 处理 keyword 变化
  const handleSetKeyword = useCallback((newKeyword: string) => {
    setKeyword(newKeyword);
  }, []);

  // 处理分类变化
  const handleSetSelectedCategory = useCallback((category: PlatformCategory) => {
    setSelectedCategory(category);
  }, []);

  /**
   * onViewMore 点击查看全部，跳转到搜索页面
   * 由首页 Banner 发起，因此语言回到英文（首页没有语言入口）
   */
  const onViewMore = useCallback(() => {
    setShowSearch(true);
    setShowGlobalSearch(false);
    setSearchLanguage(BASE_LANGUAGE);
  }, []);

  /**
   * onBack 从搜索结果返回支持首页
   *
   * 语言一并回到英文：首页语境全程英文（没有语言入口），留着上一个语境的语言
   * 只会让"首页视图下藏着一个别的语言"成为隐患；重新进结果页时 onViewMore 也会再设一次。
   */
  const onBack = useCallback(() => {
    setShowSearch(false);
    setShowGlobalSearch(false);
    setKeyword('');
    setSelectedCategory('all');
    setSearchLanguage(BASE_LANGUAGE);
    homeSearch.reset();
    paginationSearch.reset();

    // 地址栏同时还原：allResults / keyword / language 都是内页 View more 写进来的结果页语境，
    // 只重置内存状态的话地址栏仍留着 language=zh —— 首页再搜索或刷新都会又回到中文结果。
    // current=support 必须保留：它决定首页渲染帮助中心区块（见 components/Header）。
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.delete('allResults');
    searchParams.delete('keyword');
    searchParams.delete('language');
    const query = searchParams.toString();
    nav.to('/', { search: query ? `?${query}` : undefined, replace: true });
  }, [homeSearch, paginationSearch, nav]);

  return (
    <>
      {showSearch ? (
        // 结果页跟随发起端语言：共享组件（搜索框 / 按钮 / 空态等）的文案也一起切
        <LocaleProvider lang={searchLanguage}>
          <SupportSearch
            onBack={onBack}
            keyword={keyword}
            selectedCategory={selectedCategory}
            searchState={paginationSearch.state}
            searchPending={paginationSearch.pending}
            onKeywordChange={handleSetKeyword}
            onCategoryChange={handleSetSelectedCategory}
            onLoadMore={paginationSearch.loadMore}
            language={searchLanguage}
          />
        </LocaleProvider>
      ) : (
        <SupportHome
          onViewMore={onViewMore}
          searchSuggestions={searchSuggestions}
          keyword={keyword}
          searchState={homeSearch.state}
          onKeywordChange={handleSetKeyword}
          showGlobalSearch={showGlobalSearch}
          setShowGlobalSearch={setShowGlobalSearch}
        />
      )}
    </>
  );
};

export default Support;
